import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissionAccess } from './usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import { toast } from '@/utils/toast';
import {
  CsvFile,
  CsvFileVersion,
  CsvFileValidationStatus,
} from '@/types/csvFiles';
import { updateCsvFileVersionStatus } from '@/services/csvFilesApi';
import { CSV_FILES_QUERY_KEYS, useUpdateCsvFileVersionStatus } from '@/services/csvFilesHooks';

/**
 * Hook for CSV file approval functionality
 * Provides permission checking and approval/rejection handlers for files and versions
 */
export function useCsvApproval() {
  const canApprove = usePermissionAccess([PERMISSIONS.CSV_APPROVE]);
  const queryClient = useQueryClient();

  /**
   * Updates status for the latest version of a CSV file.
   * Backend contract: approving/rejecting a file means changing status of its last_version_id.
   */
  const updateFileLastVersionStatus = useCallback(
    async (file: CsvFile, status: CsvFileValidationStatus) => {
      if (!file?.id || !file?.last_version_id) {
        const error = new Error('Missing file id or last_version_id');
        toast.error('Failed to update status', { description: error.message });
        throw error;
      }

      try {
        await updateCsvFileVersionStatus(file.id, file.last_version_id, status);

        const statusLabels: Record<CsvFileValidationStatus, string> = {
          pending: 'Pending',
          approved: 'Approved',
          rejected: 'Rejected',
        };

        toast.success('Status updated', {
          description: `${file.filename} status changed to ${statusLabels[status]}`,
        });

        // Refresh list + related caches
        queryClient.invalidateQueries({ queryKey: ['csvFiles'] });
        queryClient.invalidateQueries({ queryKey: CSV_FILES_QUERY_KEYS.csvFile(file.id) });
        queryClient.invalidateQueries({
          queryKey: CSV_FILES_QUERY_KEYS.csvFileVersions(file.id),
        });
      } catch (error) {
        toast.error('Failed to update status', {
          description:
            error instanceof Error
              ? error.message
              : 'Please try again later',
        });
        throw error;
      }
    },
    [queryClient]
  );

  const approveFile = useCallback(
    async (file: CsvFile) => updateFileLastVersionStatus(file, 'approved'),
    [updateFileLastVersionStatus]
  );

  const rejectFile = useCallback(
    async (file: CsvFile) => updateFileLastVersionStatus(file, 'rejected'),
    [updateFileLastVersionStatus]
  );

  const updateValidationStatus = useCallback(
    async (file: CsvFile, status: CsvFileValidationStatus) =>
      updateFileLastVersionStatus(file, status),
    [updateFileLastVersionStatus]
  );

  // Get mutation hook for version status updates
  const updateVersionStatusMutation = useUpdateCsvFileVersionStatus();

  // Functions for CSV file versions (use mutation hook - toast is shown at mutation level)
  const approveVersion = useCallback(
    async (csvFileId: number, version: CsvFileVersion) => {
      await updateVersionStatusMutation.mutateAsync({
        csvFileId,
        versionId: version.id,
        status: 'approved',
        versionFilename: version.filename,
      });
    },
    [updateVersionStatusMutation]
  );

  const rejectVersion = useCallback(
    async (csvFileId: number, version: CsvFileVersion) => {
      await updateVersionStatusMutation.mutateAsync({
        csvFileId,
        versionId: version.id,
        status: 'rejected',
        versionFilename: version.filename,
      });
    },
    [updateVersionStatusMutation]
  );

  const updateVersionStatus = useCallback(
    async (
      csvFileId: number,
      version: CsvFileVersion,
      status: CsvFileValidationStatus
    ) => {
      await updateVersionStatusMutation.mutateAsync({
        csvFileId,
        versionId: version.id,
        status,
        versionFilename: version.filename,
      });
    },
    [updateVersionStatusMutation]
  );

  return {
    canApprove,
    // File functions (real: update last_version_id)
    approveFile,
    rejectFile,
    updateValidationStatus,
    // Version functions (real API)
    approveVersion,
    rejectVersion,
    updateVersionStatus,
  };
}

